// #765 Story D View 3 — card-type effectiveness across the whole
// population. Per card_key: dealt count, played rate, avg engagement
// when played, decline rate. Sortable by any column. Filters:
// language, cohort (cold_start / steady_state), call sequence
// (first_5 / after_5).
//
// Decision this view informs: which cards are dead weight, which are
// universally strong — cull or tier accordingly.
import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';

const VERSION = 'v1.0.0';

// Column metadata — drives table headers + per-column sort.
const COLUMNS = [
  { key: 'card_key',                  label: 'Card key',   align: 'left',  format: v => v },
  { key: 'dealt_count',               label: 'Dealt',      align: 'right', format: v => v?.toLocaleString?.() ?? v },
  { key: 'played_count',              label: 'Played',     align: 'right', format: v => v?.toLocaleString?.() ?? v },
  { key: 'played_rate',               label: 'Play rate',  align: 'right', format: v => v != null ? `${Math.round(v * 100)}%` : '—' },
  { key: 'avg_engagement_when_played',label: 'Avg eng.',   align: 'right', format: v => v != null ? Math.round(v) : '—' },
  { key: 'declined_count',            label: 'Declined',   align: 'right', format: v => v?.toLocaleString?.() ?? v },
  { key: 'decline_rate',              label: 'Decline rate', align: 'right', format: v => v != null ? `${Math.round(v * 100)}%` : '—' },
];

const COHORT_OPTIONS = [
  { value: '', label: 'All users' },
  { value: 'cold_start', label: 'Cold-start (still in exposure budget)' },
  { value: 'steady_state', label: 'Steady state' },
];

const SEQUENCE_OPTIONS = [
  { value: '', label: 'All calls' },
  { value: 'first_5', label: 'First 5 calls per user' },
  { value: 'after_5', label: 'After call #5 per user' },
];

function rateColor(rate) {
  if (rate == null) return '#888';
  if (rate >= 0.7) return '#28A745';
  if (rate >= 0.3) return '#E8845A';
  return '#DC3545';
}

function engagementColor(v) {
  if (v == null) return '#888';
  if (v >= 70) return '#28A745';
  if (v >= 30) return '#E8845A';
  return '#DC3545';
}

export default function AdminCardsEffectiveness() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [language, setLanguage] = useState('');
  const [cohort, setCohort] = useState('');
  const [sequence, setSequence] = useState('');

  const [sortKey, setSortKey] = useState('dealt_count');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const qs = new URLSearchParams();
        if (language) qs.set('language', language);
        if (cohort) qs.set('cohort', cohort);
        if (sequence) qs.set('sequence', sequence);
        const url = `/api/admin/cards/effectiveness${qs.toString() ? `?${qs}` : ''}`;
        const r = await adminFetch(url);
        if (cancelled) return;
        setRows(r?.rows || []);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [language, cohort, sequence]);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // nulls last on either sort direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [rows, sortKey, sortDir]);

  function clickSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'card_key' ? 'asc' : 'desc');
    }
  }

  return (
    <AdminLayout title="Card-type effectiveness">
      <style>{userSubPageStyles}</style>
      <style>{`
        .filter-bar {
          display: flex; gap: 12px; align-items: center;
          margin-bottom: 18px; flex-wrap: wrap;
        }
        .filter-bar label {
          font-size: 0.82rem; color: #666; font-weight: 600;
        }
        .filter-bar select, .filter-bar input {
          padding: 6px 10px; border: 1px solid #ccc;
          border-radius: 6px; font-size: 0.88rem;
          background: #fff; font-family: inherit;
        }
        .sortable-th {
          cursor: pointer; user-select: none;
        }
        .sortable-th:hover { color: #E8845A; }
        .sort-arrow {
          display: inline-block; margin-left: 4px; color: #aaa;
          font-size: 0.7rem;
        }
        .sort-arrow.active { color: #E8845A; }
      `}</style>

      <div className="page-header">
        <div className="page-title">
          Card-type effectiveness<span className="ver">{VERSION}</span>
        </div>
        <div className="page-subtitle">
          Population aggregate · which cards engage, which die · #765 Story D View 3
        </div>
      </div>

      <div className="filter-bar">
        <label>Language</label>
        <input
          type="text" value={language} placeholder="e.g. en, nl (blank = all)"
          onChange={e => setLanguage(e.target.value.trim().toLowerCase())}
          style={{ width: 180 }}
        />
        <label>Cohort</label>
        <select value={cohort} onChange={e => setCohort(e.target.value)}>
          {COHORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label>Sequence</label>
        <select value={sequence} onChange={e => setSequence(e.target.value)}>
          {SEQUENCE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && !error && rows && rows.length === 0 && (
        <div className="empty-state">No data for these filters.</div>
      )}

      {!loading && !error && rows && rows.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th
                    key={c.key}
                    className="sortable-th"
                    onClick={() => clickSort(c.key)}
                    style={{ textAlign: c.align }}
                  >
                    {c.label}
                    <span className={`sort-arrow${sortKey === c.key ? ' active' : ''}`}>
                      {sortKey === c.key
                        ? (sortDir === 'asc' ? '▲' : '▼')
                        : '↕'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => (
                <tr key={r.card_key}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.86rem' }}>
                    {/* #806 — open_thread cards have UUID suffixes;
                        backend pre-joins display_label so admins
                        see the recall topic instead of a raw GUID. */}
                    {r.display_label || r.card_key}
                  </td>
                  <td style={{ textAlign: 'right' }}>{r.dealt_count.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{r.played_count.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: rateColor(r.played_rate) }}>
                    {`${Math.round((r.played_rate || 0) * 100)}%`}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: engagementColor(r.avg_engagement_when_played) }}>
                    {r.avg_engagement_when_played != null ? Math.round(r.avg_engagement_when_played) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{r.declined_count.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', color: r.decline_rate > 0.3 ? '#C62828' : '#666' }}>
                    {`${Math.round((r.decline_rate || 0) * 100)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
